// ============================================
// src/components/payments/QuickReminderModal.js
// Quick reminder modal for Admin Dashboard
// ============================================

import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase.config';

function QuickReminderModal({ isOpen, onClose, userData }) {
  const [loading, setLoading] = useState(false);
  const [residents, setResidents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentStatuses, setPaymentStatuses] = useState({});
  const [message, setMessage] = useState('');
  const [gpayNumber, setGpayNumber] = useState('9884723782');
  const [maintenanceAmount, setMaintenanceAmount] = useState(800);
  
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                     'July', 'August', 'September', 'October', 'November', 'December'];
  const monthName = monthNames[currentMonth - 1];

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch residents
      const { data: residentData } = await supabase
        .from('residents')
        .select('*')
        .eq('account_status', 'active')
        .neq('role', 'superadmin')
        .order('flat_number');

      setResidents(residentData || []);

      // Fetch payments for current month
      const { data: paymentData } = await supabase
        .from('payments')
        .select('*')
        .eq('payment_month', currentMonth)
        .eq('payment_year', currentYear);

      setPayments(paymentData || []);

      // Generate payment status
      if (residentData) {
        generatePaymentStatus(residentData, paymentData || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePaymentStatus = (residentsList, paymentsList) => {
    const statusMap = {};
    
    // Get unique flats
    const uniqueFlats = [...new Set(residentsList.map(r => r.flat_number))].sort((a, b) => 
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );

    // Check payment status for each flat
    uniqueFlats.forEach(flatNumber => {
      const flatResidents = residentsList.filter(r => r.flat_number === flatNumber);
      const hasPaid = flatResidents.some(resident => 
        paymentsList.some(p => 
          p.resident_id === resident.id && 
          p.status === 'paid'
        )
      );
      statusMap[flatNumber] = hasPaid ? 'Paid' : '';
    });

    setPaymentStatuses(statusMap);
    generateMessage(statusMap);
  };

  const generateMessage = (statusMap = paymentStatuses) => {
    // Generate flat details string
    let flatDetails = '';
    Object.keys(statusMap).sort((a, b) => 
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    ).forEach(flatNumber => {
      const status = statusMap[flatNumber];
      flatDetails += `${flatNumber} – ${status}\n`;
    });
    
    const messageText = `The monthly maintenance charge of ₹${maintenanceAmount} started in ${monthName} ${currentYear}.
Payment Received Details!
${flatDetails.trim()}
Payment can be made via GPay to ${gpayNumber}`;
    
    setMessage(messageText);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(message).then(() => {
      alert('Message copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy: ', err);
      alert('Failed to copy message. Please select and copy manually.');
    });
  };

  const sendViaWhatsApp = () => {
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://web.whatsapp.com/send?text=${encodedMessage}`, '_blank');
  };

  const saveAndClose = async () => {
    setLoading(true);
    try {
      const totalFlats = Object.keys(paymentStatuses).length;
      const paidCount = Object.values(paymentStatuses).filter(s => s === 'Paid').length;
      const pendingCount = totalFlats - paidCount;
      
      // Save to reminder history
      const { error } = await supabase
        .from('payment_reminders')
        .insert([{
          month: currentMonth,
          year: currentYear,
          reminder_type: 'whatsapp_group',
          message: message,
          sent_at: new Date().toISOString(),
          sent_by: userData?.id,
          metadata: {
            total_flats: totalFlats,
            paid_count: paidCount,
            pending_count: pendingCount,
            gpay_number: gpayNumber,
            source: 'dashboard_quick_action'
          }
        }]);

      if (error) throw error;
      
      alert('Reminder saved successfully!');
      onClose();
    } catch (error) {
      console.error('Error saving reminder:', error);
      alert('Failed to save reminder');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Calculate statistics
  const totalFlats = Object.keys(paymentStatuses).length;
  const paidFlats = Object.values(paymentStatuses).filter(status => status === 'Paid').length;
  const pendingFlats = totalFlats - paidFlats;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '30px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'transparent',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666'
          }}
        >
          ×
        </button>

        <h2 style={{ marginBottom: '20px', color: '#333' }}>📱 Quick Payment Reminder</h2>

        {loading ? (
          <p>Loading payment data...</p>
        ) : (
          <>
            {/* Statistics */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '10px',
              marginBottom: '20px'
            }}>
              <div style={{
                background: '#e7f3ff',
                padding: '10px',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{totalFlats}</div>
                <div style={{ fontSize: '12px' }}>Total</div>
              </div>
              <div style={{
                background: '#d4edda',
                padding: '10px',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#28a745' }}>{paidFlats}</div>
                <div style={{ fontSize: '12px' }}>Paid</div>
              </div>
              <div style={{
                background: '#fff3cd',
                padding: '10px',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#ffc107' }}>{pendingFlats}</div>
                <div style={{ fontSize: '12px' }}>Pending</div>
              </div>
            </div>

            {/* Settings */}
            <div style={{
              background: '#f8f9fa',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: '#666' }}>Amount (₹)</label>
                  <input
                    type="number"
                    value={maintenanceAmount}
                    onChange={(e) => {
                      setMaintenanceAmount(e.target.value);
                      generateMessage();
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      marginTop: '5px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#666' }}>GPay Number</label>
                  <input
                    type="text"
                    value={gpayNumber}
                    onChange={(e) => {
                      setGpayNumber(e.target.value);
                      generateMessage();
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      marginTop: '5px'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Message Preview */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                Message Preview:
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                style={{
                  width: '100%',
                  minHeight: '200px',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  marginTop: '10px',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  background: '#f8f9fa'
                }}
              />
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={copyToClipboard}
                style={{
                  padding: '10px 20px',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                📋 Copy
              </button>
              
              <button
                onClick={sendViaWhatsApp}
                style={{
                  padding: '10px 20px',
                  background: '#25D366',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                📱 WhatsApp
              </button>
              
              <button
                onClick={saveAndClose}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: '600'
                }}
              >
                💾 Save & Close
              </button>
              
              <button
                onClick={onClose}
                style={{
                  padding: '10px 20px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default QuickReminderModal;